/**
 * API fetch client — C04 stub.
 *
 * TODO(C01/C03): auto-generate types from packages/shared/openapi.json once
 * C01/C03 have produced it. For now types are stubbed as record shapes.
 *
 * Injects the Firebase ID token from useIdToken() into every request.
 * Since hooks cannot be called outside React components, the client
 * accepts a `getToken` factory so callers can pass `useIdToken()`.
 */

// Typed view of Vite's import.meta.env so we avoid bare `any` casts.
interface ViteEnv {
  VITE_API_BASE_URL?: string;
  DEV?: boolean;
  MODE?: string;
}

function getViteEnv(): ViteEnv {
  // import.meta.env is injected by Vite at build time; not present in Node/vitest without Vite.

  const meta = import.meta as { env?: ViteEnv };
  return meta.env ?? {};
}

// TODO(C01/C03): replace with z.infer types from packages/shared once openapi.json exists
export interface MeResponse {
  uid: string;
  email: string | null;
  displayName: string | null;
}

// TODO(C01/C03): expand as routes are added
export interface ApiPaths {
  "/me": { response: MeResponse };
}

const API_BASE = getViteEnv().VITE_API_BASE_URL ?? "/api";

export interface ClientOptions {
  /** Function that returns the current Firebase ID token (or null). */
  getToken: () => Promise<string | null>;
}

export interface RequestOptions extends RequestInit {
  /** Additional query params to append. */
  params?: Record<string, string>;
}

/**
 * Creates a typed API client that injects the ID token as a Bearer header.
 */
export function createApiClient(options: ClientOptions) {
  async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
    const { params, ...fetchInit } = init;

    let url = `${API_BASE}${path}`;
    if (params && Object.keys(params).length > 0) {
      url += `?${new URLSearchParams(params).toString()}`;
    }

    const token = await options.getToken();
    const headers = new Headers(fetchInit.headers as HeadersInit | undefined);
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("Content-Type", "application/json");

    const response = await fetch(url, { ...fetchInit, headers });

    if (!response.ok) {
      throw new Error(`API error ${response.status}: ${response.statusText}`);
    }

    // 204 No Content (e.g. DELETE) has nothing to parse.
    if (response.status === 204) {
      return undefined as T;
    }
    return response.json() as Promise<T>;
  }

  return {
    /** GET /me — returns the authenticated user's profile. */
    me: () => request<MeResponse>("/me"),

    /** Generic request helper for future endpoints. */
    get: <T>(path: string, init?: RequestOptions) => request<T>(path, { ...init, method: "GET" }),
    post: <T>(path: string, body?: unknown, init?: RequestOptions) =>
      request<T>(path, {
        ...init,
        method: "POST",
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      }),
    patch: <T>(path: string, body: unknown, init?: RequestOptions) =>
      request<T>(path, {
        ...init,
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    delete: <T>(path: string, init?: RequestOptions) =>
      request<T>(path, { ...init, method: "DELETE" }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
